import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Star, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEEDBACK_STORAGE_KEY = 'club_feedback_state';

// Feedback frequency logic:
// - After first round scored
// - Every 3rd round after that
// - After 5th general app session (once only)
// - Never more than once per 48 hours
const shouldShowFeedback = (context) => {
  try {
    const state = JSON.parse(localStorage.getItem(FEEDBACK_STORAGE_KEY) || '{}');
    const now = Date.now();
    const lastShown = state.lastShown || 0;
    const hoursSinceShown = (now - lastShown) / (1000 * 60 * 60);

    // Never show more than once per 48 hours
    if (hoursSinceShown < 48) return false;

    if (context === 'post_score') {
      const scoreCount = (state.scoreCount || 0) + 1;
      localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify({ ...state, scoreCount }));
      // Show on 1st, then every 3rd
      return scoreCount === 1 || scoreCount % 3 === 0;
    }

    if (context === 'session') {
      const sessionCount = (state.sessionCount || 0) + 1;
      localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify({ ...state, sessionCount }));
      // Show on 5th session, then every 10th
      return sessionCount === 5 || (sessionCount > 5 && sessionCount % 10 === 0);
    }

    return false;
  } catch {
    return false;
  }
};

const markFeedbackShown = () => {
  try {
    const state = JSON.parse(localStorage.getItem(FEEDBACK_STORAGE_KEY) || '{}');
    state.lastShown = Date.now();
    localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(state));
  } catch {}
};

const FeedbackPopup = ({ context = 'general', onClose }) => {
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  const handleSubmit = async () => {
    if (rating === 0) { toast.error('Please select a rating'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/feedback`, { rating, message, context });
      toast.success('Thanks for your feedback!');
      markFeedbackShown();
      onClose();
    } catch {
      toast.error('Failed to send feedback');
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center" data-testid="feedback-popup">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4"
        style={{ background: '#fff', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg font-bold" style={{ color: 'var(--deep-forest)' }}>
              How's your experience?
            </h3>
            <button onClick={onClose} className="p-1 rounded-full" style={{ color: 'var(--warm-gray)' }}>
              <X size={18} />
            </button>
          </div>
          <p className="text-sm mb-4" style={{ color: 'var(--warm-gray)' }}>
            Your feedback helps us improve The Club for everyone.
          </p>

          {/* Star Rating */}
          <div className="flex justify-center gap-2 mb-5" data-testid="feedback-stars">
            {[1,2,3,4,5].map(s => (
              <button key={s}
                onMouseEnter={() => setHoverRating(s)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(s)}
                className="transition-transform hover:scale-110"
                data-testid={`feedback-star-${s}`}
              >
                <Star
                  size={32}
                  fill={(hoverRating || rating) >= s ? '#C8A96A' : 'transparent'}
                  stroke={(hoverRating || rating) >= s ? '#C8A96A' : '#D8D3C6'}
                  strokeWidth={1.5}
                />
              </button>
            ))}
          </div>

          {/* Message */}
          <textarea
            data-testid="feedback-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What can we improve? Any features you'd love to see?"
            rows={3}
            className="w-full px-4 py-3 rounded-xl border text-sm resize-none focus:outline-none"
            style={{
              borderColor: 'var(--fairway-sand)',
              background: 'var(--surface-secondary)',
              color: 'var(--deep-forest)',
            }}
          />

          <div className="flex gap-3 mt-4">
            <button onClick={onClose}
              className="flex-1 py-3 rounded-xl text-sm font-semibold border"
              style={{ borderColor: 'var(--fairway-sand)', color: 'var(--warm-gray)' }}
            >
              Not Now
            </button>
            <button onClick={handleSubmit} disabled={sending}
              className="flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
              style={{ background: 'var(--club-green)', color: '#fff', opacity: sending ? 0.6 : 1 }}
              data-testid="feedback-submit"
            >
              {sending ? 'Sending...' : <><Send size={14} /> Submit</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { shouldShowFeedback, markFeedbackShown };
export default FeedbackPopup;
